/**
 * TSL → generic node-graph adapter.
 *
 * Walks a three.js `NodeMaterial`'s `*Node` roots and converts the underlying
 * TSL node tree into the domain-agnostic {@link NodeGraph} model. This is the
 * first {@link GraphAdapter} and the piece that replaces the read-only
 * "TSL … (Complex Graph)" placeholder in `propertyProvider.tsl.ts`.
 *
 * Robustness notes:
 * - Introspection is done purely by **duck typing** (the `isXxxNode` guards in
 *   `helper.nodes.ts` and generic own-property enumeration) — never
 *   `instanceof`. The material may belong to the *inspected page's* three.js,
 *   which can be any version and lives in another JS realm.
 * - Child discovery mirrors three's private `Node._getChildren()`: enumerate
 *   own enumerable properties, skip leading-underscore keys, and descend into
 *   node-valued properties, arrays of nodes, and plain-object maps of nodes.
 *   Keeping the property name lets us label input sockets.
 * - Nodes are de-duplicated by `uuid`, so a shared uniform/varying becomes one
 *   node with fan-out, and back-references terminate instead of looping.
 */

import { Material } from "three";
import {
    createGraph,
    defaultTypeColor,
    type DataType,
    type GraphNode,
    type NodeGraph,
    type Socket,
} from "../../nodegraph/model.js";
import type { GraphAdapter } from "../../nodegraph/adapter.js";
import { isNodeLike, getNodeMaterials, type NodeLike, type NodeMaterialLike } from "./helper.nodes.js";
import { TSL_NODE_PROPERTY_CONFIGS } from "./propertyProvider.tsl.js";

export interface TSLGraphOptions {
    /** Hard cap on nodes to guard against pathological graphs. Default 4000. */
    maxNodes?: number;
}

/** Human-readable names for TSL {@link OperatorNodeLike} operators. */
const OPERATOR_NAMES: Record<string, string> = {
    "+": "Add", "-": "Subtract", "*": "Multiply", "/": "Divide", "%": "Modulo",
    "==": "Equal", "!=": "Not Equal", "<": "Less", ">": "Greater",
    "<=": "Less Equal", ">=": "Greater Equal",
    "&&": "And", "||": "Or", "!": "Not",
    "&": "Bit And", "|": "Bit Or", "^": "Bit Xor", "~": "Bit Not",
    "<<": "Shift Left", ">>": "Shift Right",
};

/** Root `*Node` keys, sourced from the inspector's existing config plus a fallback scan. */
const ROOT_KEYS: readonly string[] = TSL_NODE_PROPERTY_CONFIGS.map(c => c.key as string);

/**
 * A per-material identity used as the graph id (and thus the key for viewport
 * persistence). Prefers `name` (survives reloads for named/asset materials),
 * else the numeric `id` (session-stable, shared-material-safe), else `uuid`,
 * disambiguated by material `type`. `Material.uuid`/`id` regenerate on
 * construction, so unnamed materials are effectively session-scoped.
 */
function materialKey(material: Material): string {
    const m = material as { name?: string; id?: number; uuid?: string; type?: string };
    const base = m.name || (m.id != null ? String(m.id) : m.uuid) || "material";
    return `${base}:${m.type ?? "Material"}`;
}

/** The material's display name (its `name`, empty when unnamed — the type is shown separately). */
function materialName(material: Material): string {
    return (material as { name?: string }).name || "";
}

/**
 * Distinct display labels for a set of materials, for grouping (e.g. the code
 * panel's per-material sections). Each label prefers the material's `name`,
 * else its `type`; when several materials would share that label (e.g. three
 * `MeshBasicNodeMaterial`s), each is disambiguated by its numeric `id` (else a
 * 1-based index) so every material reads as its own group. Order-preserving
 * and index-aligned with the input.
 */
export function uniqueMaterialLabels(materials: readonly unknown[]): string[] {
    const bases = materials.map((mat) => {
        const m = mat as { name?: string; type?: string };
        return (typeof m.name === "string" && m.name) || m.type || "Material";
    });
    const counts = new Map<string, number>();
    for (const b of bases) counts.set(b, (counts.get(b) ?? 0) + 1);
    return bases.map((base, i) => {
        if ((counts.get(base) ?? 0) <= 1) return base;
        const id = (materials[i] as { id?: number }).id;
        return `${base} (#${id ?? i + 1})`;
    });
}

/** Converts a `NodeMaterial` into the generic node-graph model. */
export function tslMaterialToGraph(material: Material, options: TSLGraphOptions = {}): NodeGraph {
    const maxNodes = options.maxNodes ?? 4000;
    const graph = createGraph(materialKey(material), "tsl", materialName(material), material.type);

    // Synthetic sink node representing the material's outputs.
    const materialNode: GraphNode = {
        id: "material",
        sourcePath: [],
        typeRef: material.type ?? "NodeMaterial",
        title: material.type ?? "Material",
        category: "output",
        inputs: [],
        outputs: [],
    };
    graph.nodes.push(materialNode);

    const walker = createNodeWalker(graph, maxNodes);
    const walk = walker.walk;

    // Walk each assigned root and connect it into the material sink.
    for (const { key, node: value } of getMaterialNodeRoots(material)) {
        const socketId = key;
        materialNode.inputs.push({
            id: socketId,
            name: stripNodeSuffix(key),
            kind: "data",
            type: toDataType(value.nodeType),
        });
        const rootId = walk(value, [key]);
        graph.dataEdges.push({
            from: { node: rootId, socket: "out" },
            to: { node: "material", socket: socketId },
        });
    }

    // Built-shader step-in. A built-in material (MeshPhysicalNodeMaterial and
    // friends) creates its node tree INSIDE the compile and stores nothing back
    // on itself — `build()` is just `setup(builder)` — so the assigned-slot walk
    // above legitimately finds nothing and the card reads as a dead end. The
    // builder three retains after that compile is the only place the generated
    // tree survives, so offer it as a drill-down when it happens to be there.
    if (hasBuiltNodeGraph(material)) {
        const ref = new WeakRef(material as object);
        materialNode.meta = { canStepToBuilt: true, builtMaterial: { deref: () => ref.deref() } };
    }

    if (walker.isTruncated()) graph.name = `${graph.name ?? material.type} (truncated at ${maxNodes} nodes)`;
    labelLeafOutputs(graph);
    return graph;
}

// #region Built (compiled) shader graph

/**
 * The `NodeBuilder` three retains on a material after its last compile. three's
 * own WebGL nodes handler both writes and reads this property
 * (`material._latestBuilder`, examples/jsm/tsl/WebGLNodesHandler.js), which is
 * why the NAME is safe to rely on — its PRESENCE is not: it only exists once the
 * material has actually been built, and backends that discard their builder
 * never set it. Absent always means "no built graph", never an error.
 */
interface BuiltNodeBuilderLike {
    nodes?: unknown;
    context?: Record<string, unknown>;
    [key: string]: unknown;
}

function getLatestNodeBuilder(material: unknown): BuiltNodeBuilderLike | null {
    const m = material as Record<string, unknown> | null | undefined;
    if (!m || typeof m !== "object") return null;
    // `_latestBuilder` is three's own name; the un-underscored spelling is
    // accepted too so a fork/proxy that re-exposes it still works.
    const builder = (m._latestBuilder ?? m.latestBuilder) as BuiltNodeBuilderLike | null | undefined;
    return builder && typeof builder === "object" ? builder : null;
}

/** The built nodes the builder collected during the compile (three's `addNode`). */
function builtNodeSet(builder: BuiltNodeBuilderLike): NodeLike[] {
    const raw = builder.nodes as Iterable<unknown> | null | undefined;
    if (!raw || typeof (raw as Iterable<unknown>)[Symbol.iterator] !== "function") return [];
    const out: NodeLike[] = [];
    for (const node of raw) if (isNodeLike(node)) out.push(node);
    return out;
}

/** **Cheap.** True when {@link builtMaterialToGraph} has something to show —
 *  safe to call while building every material graph (Set `size` fast path). */
export function hasBuiltNodeGraph(material: unknown): boolean {
    const builder = getLatestNodeBuilder(material);
    if (!builder) return false;
    const size = (builder.nodes as { size?: unknown } | null | undefined)?.size;
    if (typeof size === "number") return size > 0;
    return builtNodeSet(builder).length > 0;
}

/** Builder properties that hold a stage's final node when three parks one there. */
const BUILT_ROOT_KEYS: readonly string[] = ["outputNode", "fragmentNode", "vertexNode"];

/**
 * Every node value one level down, **including `_`-prefixed keys**. Used only to
 * mark nodes as "referenced" when deriving roots: {@link getChildNodes} skips
 * private keys on purpose (they are not graph edges), but a node held *only*
 * privately by another node is still not a root — counting it as one would turn
 * most of the tree into spurious roots.
 */
function* allReferencedNodes(node: NodeLike): Iterable<NodeLike> {
    for (const key of Object.keys(node)) {
        const value = (node as unknown as Record<string, unknown>)[key];
        if (isNodeLike(value)) {
            yield value;
        } else if (Array.isArray(value)) {
            for (const item of value) {
                if (isNodeLike(item)) yield item;
                else if (isPlainObject(item)) yield* nodeValuesOf(item);
            }
        } else if (isPlainObject(value)) {
            yield* nodeValuesOf(value);
        }
    }
}
function* nodeValuesOf(record: Record<string, unknown>): Iterable<NodeLike> {
    for (const key of Object.keys(record)) {
        const value = record[key];
        if (isNodeLike(value)) yield value;
    }
}

/**
 * The per-stage node flows three records while compiling — `builder.flowNodes`
 * is `{ vertex: [], fragment: [], compute: [] }`, appended to by
 * `addFlow(stage, node)`, so the LAST entry of a stage is that stage's final
 * output. This is the root we actually want and the reason it beats deriving
 * one: `builder.nodes` also holds every statement CONTAINER the compiler made
 * (StackNode/ContextNode/AssignNode), and those are referenced only from
 * builder-side arrays — so a "nothing references it" rule crowns every one of
 * them a root and the material card grows hundreds of `output` sockets.
 */
function flowRoots(builder: BuiltNodeBuilderLike): { key: string; node: NodeLike }[] {
    const flows = builder.flowNodes as Record<string, unknown> | null | undefined;
    if (!flows || typeof flows !== "object") return [];
    const out: { key: string; node: NodeLike }[] = [];
    for (const stage of Object.keys(flows)) {
        const list = flows[stage];
        if (!Array.isArray(list)) continue;
        const staged = list.filter(isNodeLike);
        if (!staged.length) continue;
        // A stage can hold SEVERAL flows (`addFlow` appends), so taking only the
        // last would silently drop independent outputs. Take them all, minus any
        // that another flow already contains — usually that leaves the single
        // statement block, without hiding a genuine second output.
        const contained = new Set<string>();
        for (const node of staged) {
            for (const reached of reachableFrom(node, MAX_BUILT_GRAPH_NODES)) {
                if (reached !== node.uuid) contained.add(reached);
            }
        }
        for (const node of staged) {
            if (!contained.has(node.uuid)) out.push({ key: stage, node });
        }
    }
    return out;
}

/** uuids reachable from `node`, bounded. Shared by flow-root pruning and root ranking. */
function reachableFrom(node: NodeLike, limit: number): Set<string> {
    const seen = new Set<string>([node.uuid]);
    const stack: NodeLike[] = [node];
    while (stack.length && seen.size < limit) {
        const current = stack.pop()!;
        for (const child of allReferencedNodes(current)) {
            if (seen.has(child.uuid)) continue;
            seen.add(child.uuid);
            stack.push(child);
        }
    }
    return seen;
}

/**
 * Ceiling on DERIVED roots. Derivation is the last resort and it degrades badly
 * (see {@link flowRoots}); past a handful of roots it is reporting plumbing, not
 * outputs, so keep only the largest subtrees rather than rendering a wall of
 * sockets nobody can read.
 */
const MAX_DERIVED_ROOTS = 8;

/**
 * The output node(s) of a compiled material, best source first: three's own
 * per-stage flows, then named builder/context properties, then — only if both
 * are missing — a bounded derivation.
 */
function builtRoots(builder: BuiltNodeBuilderLike): { key: string; node: NodeLike }[] {
    const out: { key: string; node: NodeLike }[] = [];
    const seen = new Set<string>();
    const push = (key: string, value: unknown): void => {
        if (!isNodeLike(value) || seen.has(value.uuid)) return;
        seen.add(value.uuid);
        out.push({ key, node: value });
    };

    for (const { key, node } of flowRoots(builder)) push(key, node);
    if (out.length) return out;

    for (const key of BUILT_ROOT_KEYS) {
        push(key, builder[key]);
        push(key, builder.context?.[key]);
    }
    if (out.length) return out;

    const nodes = builtNodeSet(builder);
    const referenced = new Set<string>();
    for (const node of nodes) {
        for (const child of allReferencedNodes(node)) referenced.add(child.uuid);
    }
    const derived = nodes.filter(node => !referenced.has(node.uuid));
    const ranked = derived.length > MAX_DERIVED_ROOTS
        ? derived
            .map(node => ({ node, size: reachableFrom(node, MAX_BUILT_GRAPH_NODES).size }))
            .sort((a, b) => b.size - a.size)
            .slice(0, MAX_DERIVED_ROOTS)
            .map(entry => entry.node)
        : derived;
    for (const node of ranked) push("output", node);
    return out;
}

/**
 * Cap for the BUILT graph. A MeshPhysicalNodeMaterial compiles to roughly 1800
 * nodes, so this is set to take the whole thing rather than truncate the
 * interesting part — the earlier 800 cut a physical material in half.
 *
 * Note what this ceiling does and does not buy: it bounds NODES, not edges, and
 * edge count is what actually made the first version unreadable. Density is
 * addressed by rooting at the real stage outputs (see {@link flowRoots}), not
 * here. The walker's overflow marker states the cut when it happens.
 */
export const MAX_BUILT_GRAPH_NODES = 4000;

/**
 * The material's **compiled** node tree, from the builder three retained after
 * its last build — the internals of a built-in NodeMaterial that
 * {@link tslMaterialToGraph} cannot see because they never touch the material.
 * Null when no builder was retained or it yielded no roots.
 */
export function builtMaterialToGraph(material: Material, options: TSLGraphOptions = {}): NodeGraph | null {
    const builder = getLatestNodeBuilder(material);
    if (!builder) return null;
    const roots = builtRoots(builder);
    if (!roots.length) return null;

    const maxNodes = options.maxNodes ?? MAX_BUILT_GRAPH_NODES;
    const graph = createGraph(`${materialKey(material)}:built`, "tsl", materialName(material), material.type);
    const sink: GraphNode = {
        id: "material",
        typeRef: material.type ?? "NodeMaterial",
        title: material.type ?? "Material",
        category: "output",
        inputs: [],
        outputs: [],
    };
    graph.nodes.push(sink);

    const walker = createNodeWalker(graph, maxNodes);
    // Stage roots carry distinct keys ("fragment"/"vertex"), derived roots all
    // share "output" — suffix only on collision so the common case stays readable.
    const usedSocketIds = new Set<string>();
    const uniqueSocketId = (key: string): string => {
        let id = key;
        for (let i = 1; usedSocketIds.has(id); i++) id = `${key}#${i}`;
        usedSocketIds.add(id);
        return id;
    };
    roots.forEach(({ key, node }) => {
        const socketId = uniqueSocketId(key);
        sink.inputs.push({
            id: socketId,
            name: stripNodeSuffix(key),
            kind: "data",
            type: toDataType(node.nodeType),
        });
        const rootId = walker.walk(node);
        graph.dataEdges.push({
            from: { node: rootId, socket: "out" },
            to: { node: "material", socket: socketId },
        });
    });

    if (walker.isTruncated()) graph.name = `${graph.name ?? material.type} (truncated at ${maxNodes} nodes)`;
    labelLeafOutputs(graph);
    return graph;
}

/** Recursively walks a TSL node tree into a {@link NodeGraph}, deduped by uuid,
 *  with a shared overflow marker once `maxNodes` is reached. Shared by the
 *  material adapter and the Fn-body expander (see {@link fnCallToGraph}). */
interface NodeWalker {
    /** Converts a node (and its subtree), returning its graph-node id. */
    walk(node: NodeLike, sourcePath?: readonly string[]): string;
    isTruncated(): boolean;
}
function createNodeWalker(graph: NodeGraph, maxNodes: number): NodeWalker {
    const visited = new Map<string, string>(); // uuid → graph node id
    let counter = 0;
    let truncated = false;

    // three RESOURCES referenced by pass nodes (scene/camera/renderTarget) —
    // not TSL nodes (a RenderTarget has no uuid), so they get synthetic ids,
    // dedup by object identity, and a weak `meta.resource` like the
    // rendergraph's resource nodes (click-to-select works the same way).
    const resourceIds = new Map<object, string>();
    const addPassResource = (owner: GraphNode, value: unknown, kind: "scene" | "camera" | "target", socket: string): void => {
        if (!value || typeof value !== "object") return;
        if (graph.nodes.length >= maxNodes - 1) return; // never displace TSL nodes
        let id = resourceIds.get(value as object);
        if (!id) {
            id = `r${counter++}`;
            resourceIds.set(value as object, id);
            const record = value as { name?: string; type?: string; width?: number; height?: number };
            const title = kind === "target"
                ? (record.width && record.height ? `Target ${record.width}×${record.height}` : "Render Target")
                : (record.name || record.type || kind);
            const ref = new WeakRef(value as object);
            const meta: Record<string, unknown> = {
                resource: { kind, type: record.type, deref: () => ref.deref() },
            };
            // A render target is previewable through its TEXTURE (isTexture
            // lives there, not on the target — which is why these cards used to
            // show a bare title next to pass cards full of pictures). Handing
            // over the texture rather than a rendered thumbnail keeps this
            // adapter renderer-free: the preview service resolves the owning
            // renderer itself. READ-ONLY on purpose — no editableNode, so the
            // card shows the image without the texture-swap widget a uniform gets.
            if (kind === "target") {
                const texture = (value as { texture?: { isTexture?: boolean } }).texture;
                if (texture?.isTexture) {
                    const textureRef = new WeakRef(texture as object);
                    meta.mediaValue = true;
                    meta.previewTexture = () => textureRef.deref();
                    if (record.width && record.height) {
                        meta.thumbnailAspect = record.width / record.height;
                    }
                }
            }
            graph.nodes.push({
                id,
                typeRef: `three/${kind}`,
                title,
                category: kind,
                inputs: [],
                outputs: [{ id: "out", name: "out", kind: "data" }],
                meta,
            });
        }
        owner.inputs.push({ id: socket, name: socket, kind: "data" });
        graph.dataEdges.push({ from: { node: id, socket: "out" }, to: { node: owner.id, socket } });
    };

    // A single shared "overflow" node absorbs everything past the cap; one slot
    // is reserved for it so the graph never exceeds `maxNodes` nodes.
    let overflowId: string | null = null;
    const overflow = (): string => {
        if (overflowId) return overflowId;
        truncated = true;
        overflowId = "overflow";
        graph.nodes.push({
            id: overflowId, typeRef: "…", title: "… more nodes hidden", category: "node",
            inputs: [], outputs: [{ id: "out", name: "out", kind: "data" }],
            meta: { truncated: true },
        });
        return overflowId;
    };

    const walk = (node: NodeLike, sourcePath?: readonly string[]): string => {
        const existing = visited.get(node.uuid);
        if (existing) return existing;

        // Elide anonymous VarNodes (compiler-inserted variable/reuse markers) and
        // show the node they wrap directly — that's what carries meaning (e.g. the
        // MathNode). Named vars are kept. Dedup by uuid preserves shared reuse.
        const elided = elideAnonymousVars(node);
        if (elided !== node) {
            let current = node;
            let path = sourcePath;
            while (current !== elided) {
                current = asRecord(current).node as NodeLike;
                path = path ? [...path, "node"] : undefined;
            }
            return walk(elided, path);
        }

        // Reserve one slot for the overflow marker.
        if (graph.nodes.length >= maxNodes - 1) return overflow();

        const id = `n${counter++}`;
        visited.set(node.uuid, id);

        const gnode: GraphNode = {
            id,
            ...(sourcePath ? { sourcePath } : {}),
            typeRef: node.type ?? "Node",
            title: nodeTitle(node),
            category: nodeCategory(node),
            inputs: [],
            outputs: [outSocket(node)],
            meta: buildMeta(node),
        };
        graph.nodes.push(gnode);

        // Discover child nodes and wire data edges (child.out → this.input).
        const convertsTo = convertTargetType(node);
        for (const { property, child, segments } of getChildNodes(node)) {
            const socketId = property;
            // A ConvertNode's single input is the "from" type — label it with the
            // source type when known (so the card reads e.g. vec3 → "Convert to Float").
            // Nested paths ("params[0].start", "context.renderPipeline") label
            // with their LEAF segment — the full path stays in the socket id.
            // A StackNode's `nodes[i]` sockets are the shader body's STATEMENTS in
            // execution order. The index says nothing, so look through the
            // assignment to what it assigns — the stack card then reads as a table
            // of contents for the block ("Clearcoat", "ClearcoatRoughness", …).
            // Statements that aren't named assignments (control flow, bare
            // expressions) keep their index label, so expect a mix.
            // A statement that isn't a named assignment (a bare expression, control
            // flow) has no target to borrow a name from — but it still IS something,
            // and the card already knows what: its title. "Multiply" / "Fn: …" says
            // strictly more than "nodes[43]". Scoped to STACK entries by the
            // `nodes[i]` property shape, because everywhere else the property name
            // is the meaningful label (an operator's "a"/"b", an assign's "source")
            // and must not be replaced by the child's title.
            const isStatement = /^nodes\[\d+\]$/.test(property);
            // Name the socket after the node the EDGE comes from — `walk` elides
            // anonymous vars, so titling the raw child labelled statements after
            // wrappers that never appear on the canvas.
            const labelled = elideAnonymousVars(child);
            const statementName = assignedName(labelled) ?? (isStatement ? nodeTitle(labelled) : null);
            // A stack's `outputNode` is what the whole block RESULTS in, and in
            // compiled material code that is a named var/varying —
            // "v_positionWorld" is the one identifier a shader author would
            // recognise, where "output" says only that a stack has an output.
            // Anonymous vars keep the property label: elideAnonymousVars already
            // skipped them, so there is no name to borrow.
            const outputName = property === "outputNode" ? selfName(labelled) : null;
            const inputName = convertsTo
                ? (typeof child.nodeType === "string" && child.nodeType ? child.nodeType : "")
                : (statementName ?? outputName ?? stripNodeSuffix(property.split(".").pop() ?? property));
            gnode.inputs.push({
                id: socketId,
                name: inputName,
                kind: "data",
                type: toDataType(child.nodeType),
            });
            const childId = walk(child, sourcePath ? [...sourcePath, ...segments] : undefined);
            graph.dataEdges.push({
                from: { node: childId, socket: "out" },
                to: { node: id, socket: socketId },
            });
        }

        // A PassNode renders a scene with a camera into a render target —
        // surface those as resource NODES wired in (same anatomy as the
        // rendergraph's pass cards), not as text inside the card.
        if (asRecord(node).isPassNode === true) {
            const r = asRecord(node);
            addPassResource(gnode, r.scene, "scene", "scene");
            addPassResource(gnode, r.camera, "camera", "camera");
            addPassResource(gnode, r.renderTarget, "target", "renderTarget");
        }

        return gnode.id;
    };

    return { walk, isTruncated: () => truncated };
}

// #region Fn (function) body expansion

/**
 * Everything needed to expand a TSL `Fn` body on demand. The body is a JS
 * closure that only becomes nodes when run through a NodeBuilder, and three
 * discards that builder after compiling — so we rebuild the body ourselves,
 * using the **host renderer's own backend** (its module's stack machinery, not
 * our bundled three's, which can't interoperate with the page's node objects).
 * All fields are the live host objects (derefed from the rendergraph capture).
 */
export interface FnBuildContext {
    /** Host renderer with `.backend.createNodeBuilder` — runs the Fn body. */
    renderer: unknown;
    /** Host scene — used to find a mesh that uses the material (builder needs an object). */
    scene: unknown;
    camera?: unknown;
    /** The NodeMaterial the Fn belongs to. */
    material: unknown;
}

/** True for a TSL `Fn` call node whose body can be expanded (a ShaderCallNodeInternal).
 *  Detected purely by duck-typing the always-present flag + `shaderNode` — NOT by
 *  the `getOutputNode` method, whose presence/name varies across three versions
 *  (the expander tries both `getOutputNode` and the raw `jsFunc`). */
export function isFnCallNode(node: unknown): boolean {
    const r = node as Record<string, unknown> | null | undefined;
    if (!r || typeof r !== "object") return false;
    const looksLikeCall = r.isShaderCallNodeInternal === true
        || (typeof r.type === "string" && r.type.includes("ShaderCall"));
    return looksLikeCall && r.shaderNode != null;
}

/** First object in the scene subtree whose material is (or contains) `material`. */
function findMeshWithMaterial(scene: unknown, material: unknown): unknown {
    let found: unknown = null;
    const traverse = (scene as { traverse?: (cb: (o: unknown) => void) => void } | null)?.traverse;
    if (typeof traverse !== "function") return null;
    traverse.call(scene, (o: unknown) => {
        if (found) return;
        const m = (o as { material?: unknown }).material;
        if (m === material || (Array.isArray(m) && m.includes(material))) found = o;
    });
    return found;
}

/**
 * Expands an `Fn` call's body by running it through a throwaway NodeBuilder from
 * the HOST renderer's backend. Fully isolated and guarded: it constructs a fresh
 * builder (so the host's own `setCurrentStack`/stack machinery drives the closure),
 * builds only this call's output node, and **never writes back** to the material.
 * Returns the body's root node, or null on ANY failure — complex Fns that need
 * lights/clipping/backend context simply fall back to null. Since it may run the
 * inspected page's own code, the try/catch is load-bearing, not decorative.
 */
/** The throwaway builder handed to an expansion — three's NodeBuilder, ducked. */
type ThrowawayBuilder = Record<string, unknown> & {
    context?: Record<string, unknown>;
    addStack?: () => void;
    removeStack?: () => unknown;
};

/**
 * Runs `use` against a fresh NodeBuilder from the HOST renderer's backend, wired
 * to the material's scene/camera — the isolation every body expansion needs:
 * nothing is written back to the material, and ANY failure (including code from
 * the inspected page) becomes null so the caller can toast.
 *
 * Shared because expanding an `Fn` body and expanding a `Loop` body differ in
 * exactly one step — what they ask the node for — and nothing else.
 */
function withNodeBuilder(ctx: FnBuildContext, use: (builder: ThrowawayBuilder) => unknown): NodeLike | null {
    const renderer = ctx.renderer as { backend?: { createNodeBuilder?: (o: unknown, r: unknown) => unknown } } | null;
    const backend = renderer?.backend;
    if (!backend || typeof backend.createNodeBuilder !== "function") return null;
    const object = findMeshWithMaterial(ctx.scene, ctx.material);
    if (!object) return null;
    try {
        const builder = backend.createNodeBuilder(object, renderer) as ThrowawayBuilder;
        builder.scene = ctx.scene;
        builder.material = ctx.material;
        if (ctx.camera) builder.camera = ctx.camera;
        if (builder.context && typeof builder.context === "object") builder.context.material = ctx.material;
        const body = use(builder);
        return isNodeLike(body) ? body : null;
    } catch {
        return null;
    }
}

export function expandFnCall(fnCallNode: unknown, ctx: FnBuildContext): NodeLike | null {
    return withNodeBuilder(ctx, (builder) => {
        const node = fnCallNode as {
            getOutputNode?: (b: unknown) => unknown;
            inputNodes?: unknown;
            rawInputs?: unknown;
            shaderNode?: { jsFunc?: (...args: unknown[]) => unknown };
        };
        let body: unknown = null;
        if (typeof node.getOutputNode === "function") {
            // Preferred path (handles layout'd Fns + stacks via the node's own logic).
            body = node.getOutputNode(builder);
        } else if (typeof node.shaderNode?.jsFunc === "function") {
            // Version fallback (this three has no getOutputNode): run the closure
            // with a host stack pushed, mirroring ShaderCallNodeInternal.setupOutput.
            // Inputs live on `inputNodes` (older) or `rawInputs` (newer); an empty
            // list means "no args", which three calls as jsFunc(builder) — NOT
            // jsFunc([], builder) (that would pass [] where the builder is expected).
            const rawInputs = node.inputNodes ?? node.rawInputs;
            const hasInputs = rawInputs != null && !(Array.isArray(rawInputs) && rawInputs.length === 0);
            const hasStack = typeof builder.addStack === "function" && typeof builder.removeStack === "function";
            if (hasStack) builder.addStack!();
            try {
                body = hasInputs
                    ? node.shaderNode.jsFunc(rawInputs, builder)
                    : node.shaderNode.jsFunc(builder);
            } finally {
                if (hasStack) builder.removeStack!();
            }
        }
        return body;
    });
}

/**
 * True for a three `LoopNode`.
 *
 * By TYPE, because three gives LoopNode no `isLoopNode` flag — it identifies
 * through `static get type() { return 'LoopNode' }`, which is also where the
 * card's "Loop" title comes from. Not by shape: plenty of nodes carry a
 * `params` array and only a loop builds a body from it.
 */
export function isLoopNode(node: unknown): boolean {
    if (!node || typeof node !== "object") return false;
    return (node as { type?: unknown }).type === "LoopNode";
}

/**
 * Expands a `Loop`'s BODY into its root node.
 *
 * A loop body is not retained as nodes: three runs the callback inside
 * `getProperties(builder)`, which pushes a stack, calls the body into it and
 * returns `{ stackNode, returnsNode }`. So we ask the node for exactly that and
 * take its stack — no stack juggling of our own, and the same throwaway-builder
 * isolation the Fn path uses.
 */
export function expandLoopBody(loopNode: unknown, ctx: FnBuildContext): NodeLike | null {
    return withNodeBuilder(ctx, (builder) => {
        const node = loopNode as { getProperties?: (b: unknown) => { stackNode?: unknown } | undefined };
        if (typeof node?.getProperties !== "function") return null;
        return node.getProperties(builder)?.stackNode ?? null;
    });
}

/**
 * Expands a TSL `Fn` call node's body into its own {@link NodeGraph} drill-down
 * level. Returns null when the body can't be built (see {@link expandFnCall}) —
 * the caller shows a toast in that case.
 */
export function fnCallToGraph(
    fnCallNode: unknown,
    ctx: FnBuildContext,
    title: string,
    options: TSLGraphOptions = {},
): NodeGraph | null {
    const body = expandFnCall(fnCallNode, ctx);
    if (!body) return null;
    const uuid = (fnCallNode as { uuid?: string }).uuid;
    return bodyToGraph(body, `fn:${uuid ?? title}`, title, "Function", options);
}

/**
 * Expands a `Loop`'s body into its own drill-down level — the Fn level's twin,
 * since the body IS the loop's block of statements. Null when it can't be built
 * (see {@link expandLoopBody}); the caller toasts.
 */
export function loopBodyToGraph(
    loopNode: unknown,
    ctx: FnBuildContext,
    title: string,
    options: TSLGraphOptions = {},
): NodeGraph | null {
    const body = expandLoopBody(loopNode, ctx);
    if (!body) return null;
    const uuid = (loopNode as { uuid?: string }).uuid;
    return bodyToGraph(body, `loop:${uuid ?? title}`, title, "Loop", options);
}

/** One expanded body → one drill-down graph: walk it, hang it off a synthetic
 *  sink, label the leaves. Shared so an Fn level and a Loop level cannot drift. */
function bodyToGraph(
    body: NodeLike,
    id: string,
    title: string,
    kind: string,
    options: TSLGraphOptions,
): NodeGraph {
    const maxNodes = options.maxNodes ?? 4000;
    const graph = createGraph(id, "tsl", title, kind);
    // Synthetic sink: the value the body results in.
    const output: GraphNode = {
        id: "output", typeRef: `${kind}Return`, title: "returns", category: "output",
        inputs: [{ id: "in", name: "", kind: "data" }], outputs: [],
    };
    graph.nodes.push(output);
    const walker = createNodeWalker(graph, maxNodes);
    const rootId = walker.walk(body);
    graph.dataEdges.push({ from: { node: rootId, socket: "out" }, to: { node: "output", socket: "in" } });
    labelLeafOutputs(graph);
    return graph;
}

/**
 * Labels each leaf (uniform/constant) node's `out` socket with the name(s) of
 * the slot(s) it feeds — a uniform driving `roughness` reads "roughness" at its
 * output — and sets a socket tooltip listing the full destinations. The generic
 * "out" carries no information (the renderer hides it), the destination does.
 */
function labelLeafOutputs(graph: NodeGraph): void {
    for (const node of graph.nodes) {
        if (node.category !== "input") continue;
        const out = node.outputs.find(s => s.id === "out");
        if (!out || out.name !== "out") continue; // keep real names (e.g. split components)
        const destinations = graph.dataEdges
            .filter(e => e.from.node === node.id)
            .map(e => {
                const target = graph.nodes.find(n => n.id === e.to.node);
                const socket = target?.inputs.find(s => s.id === e.to.socket);
                return { title: target?.title ?? e.to.node, slot: socket?.name || e.to.socket };
            });
        if (!destinations.length) continue;
        const slots = [...new Set(destinations.map(d => d.slot))];
        out.name = slots.slice(0, 2).join(", ") + (slots.length > 2 ? ", …" : "");
        out.tooltip = `Connected to ${destinations.map(d => `${d.title}.${d.slot}`).join(", ")}`;
    }
}

/**
 * Safety ceiling on the *total* nodes across all resolved graphs, so a huge
 * multi-material / multi-select selection can't freeze the renderer. Generous —
 * far more than is legible — it only guards against pathological cases. Each
 * individual graph is separately capped inside {@link tslMaterialToGraph}.
 */
export const MAX_TOTAL_GRAPH_NODES = 10000;

export interface ResolvedMaterialGraphs {
    graphs: NodeGraph[];
    /** How many material graphs were omitted because {@link MAX_TOTAL_GRAPH_NODES} was hit. */
    skipped: number;
}

/**
 * **Cheap.** Collects the unique NodeMaterials exposed by a selection (a single
 * target or an array of targets), **de-duplicated by reference** so a material
 * shared across objects — or repeated in one object's material array — appears
 * once. Does NOT walk any node tree, so it is safe to run on every selection
 * change to decide whether/what the panel shows.
 */
export function collectNodeMaterials(targets: unknown): NodeMaterialLike[] {
    const list = Array.isArray(targets) ? targets : [targets];
    const seen = new Set<object>();
    const materials: NodeMaterialLike[] = [];
    for (const target of list) {
        for (const material of getNodeMaterials(target)) {
            if (seen.has(material)) continue;
            seen.add(material);
            materials.push(material);
        }
    }
    return materials;
}

/**
 * **Expensive.** Builds one graph per material by walking each node tree, stopping
 * once the total node count exceeds {@link MAX_TOTAL_GRAPH_NODES} (reporting how
 * many were `skipped`). Defer this until a view actually needs to render.
 */
export function buildMaterialGraphs(materials: readonly NodeMaterialLike[]): ResolvedMaterialGraphs {
    const graphs: NodeGraph[] = [];
    let totalNodes = 0;
    let skipped = 0;
    for (const material of materials) {
        if (totalNodes >= MAX_TOTAL_GRAPH_NODES) {
            skipped++;
            continue;
        }
        const graph = tslMaterialToGraph(material);
        graphs.push(graph);
        totalNodes += graph.nodes.length;
    }
    return { graphs, skipped };
}

/** Convenience: {@link collectNodeMaterials} + {@link buildMaterialGraphs} in one call. */
export function resolveMaterialGraphs(targets: unknown): ResolvedMaterialGraphs {
    return buildMaterialGraphs(collectNodeMaterials(targets));
}

/** The TSL {@link GraphAdapter} instance. */
export const tslGraphAdapter: GraphAdapter<Material> = {
    domain: "tsl",
    toGraph: (material) => tslMaterialToGraph(material),
    typeColor: tslTypeColor,
};

// #region Introspection helpers

/** A UniformGroupNode is buffer-grouping metadata (the `groupNode` a uniform
 * belongs to), not a dataflow node — so it shouldn't become a graph node. */
function isRenderableChild(value: unknown): value is NodeLike {
    return isNodeLike(value) && !asRecord(value).isUniformGroup;
}

/**
 * The material's assigned `*Node` roots (inspector config keys + a fallback
 * scan of own `*Node` properties), deduped by key. Shared by the graph builder
 * and the TSL source emitter (`nodegraph.tsl.code.ts`).
 */
export function getMaterialNodeRoots(material: Material): { key: string; node: NodeLike }[] {
    const out: { key: string; node: NodeLike }[] = [];
    const seen = new Set<string>();
    for (const key of [...ROOT_KEYS, ...ownNodeKeys(material)]) {
        if (seen.has(key)) continue;
        seen.add(key);
        const value = (material as unknown as Record<string, unknown>)[key];
        if (isNodeLike(value)) out.push({ key, node: value });
    }
    return out;
}

/** Yields `{ property, child }` for every node-valued own property (mirrors `_getChildren`). */
export function* getChildNodes(node: NodeLike): Iterable<{ property: string; segments: string[]; child: NodeLike }> {
    // A ShaderCall (Fn Call) node holds its function DEFINITION in `shaderNode`.
    // That's internal plumbing — the call already represents the function (args
    // come from inputNodes/rawInputs; the body via step-in / Function Source), so
    // surfacing the definition as a child produced a redundant "Fn" node wired in
    // on a "shader" socket. Skip it; genuine Fn *definition* nodes are unaffected.
    const isShaderCall = asRecord(node).isShaderCallNodeInternal === true
        || (typeof node.type === "string" && node.type.includes("ShaderCall"));
    for (const key of Object.keys(node)) {
        if (key.startsWith("_")) continue;
        if (isShaderCall && key === "shaderNode") continue;
        // `parent` is a BACK-reference (a StackNode's enclosing stack), never a
        // dataflow input. Drawing it produced an edge from every statement block
        // back across the graph to its parent — the long-haul spaghetti in a
        // compiled material, and edges the layout cannot place sensibly. Root
        // derivation still counts it (see allReferencedNodes), so stacks don't
        // become roots just because their only referrer is structural.
        if (key === "parent") continue;
        const value = (node as unknown as Record<string, unknown>)[key];
        if (isRenderableChild(value)) {
            yield { property: key, segments: [key], child: value };
        } else if (Array.isArray(value)) {
            for (let i = 0; i < value.length; i++) {
                const item = value[i];
                if (isRenderableChild(item)) {
                    yield { property: `${key}[${i}]`, segments: [key, String(i)], child: item as NodeLike };
                } else if (isPlainObject(item)) {
                    // e.g. LoopNode's params: [{ start: Node, end: Node,
                    // condition: "<", type: "int" }, fn] — the range nodes live
                    // one level down in a plain object inside the array.
                    for (const k of Object.keys(item)) {
                        const v = item[k];
                        if (isRenderableChild(v)) yield { property: `${key}[${i}].${k}`, segments: [key, String(i), k], child: v };
                    }
                }
            }
        } else if (isPlainObject(value)) {
            for (const k of Object.keys(value)) {
                const v = (value as Record<string, unknown>)[k];
                if (isRenderableChild(v)) yield { property: `${key}.${k}`, segments: [key, k], child: v };
            }
        }
    }
}

/** Own enumerable keys ending in `Node` whose value is a node (fallback beyond ROOT_KEYS). */
function ownNodeKeys(material: Material): string[] {
    const out: string[] = [];
    for (const key of Object.keys(material)) {
        if (key.endsWith("Node") && isNodeLike((material as unknown as Record<string, unknown>)[key])) out.push(key);
    }
    return out;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && Object.getPrototypeOf(value) === Object.prototype;
}

// #region Node → display mapping

// Real three.js r185 nodes do NOT reliably set the `isXxxNode` flags (e.g.
// `OperatorNode` leaves `isOperatorNode` unset), so kinds are detected from the
// always-present constructor-name `type` plus structural fields (`op`, `value`,
// `attributeName`) rather than from those flags.

const asRecord = (node: NodeLike) => node as unknown as Record<string, unknown>;

/**
 * The name a statement assigns to, for an AssignNode whose target is a named
 * node (a PropertyNode, usually — "Clearcoat", "ClearcoatRoughness"). Null for
 * anything else, including assignments to an unnamed or computed target.
 *
 * This is what makes a StackNode legible: a stack's `nodes[i]` are its
 * statements in execution order, and without looking THROUGH the assignment to
 * its target, the shader body renders as `nodes[0]…nodes[94]` wired to a row of
 * identical "Assign" cards.
 */
/**
 * The node a walk actually LANDS on. Anonymous VarNodes are compiler-inserted
 * reuse markers with nothing to say, so the walker skips through them and the
 * graph node is whatever they wrap.
 *
 * Naming has to resolve through the SAME elision, or a socket describes a node
 * that isn't in the graph: every such statement in a compiled material read
 * "Var" while its edge came from the `Fn: sRGBTransferOETF` / `Join` inside it.
 * One helper for both so the label and the edge cannot drift apart again.
 */
function elideAnonymousVars(node: NodeLike): NodeLike {
    let current = node;
    const seen = new Set<NodeLike>();
    // Bounded: vars can nest, but a cycle here would hang the whole graph build.
    for (let depth = 0; depth < 8; depth++) {
        if (seen.has(current)) return node;
        seen.add(current);
        const r = asRecord(current);
        if (r.isVarNode !== true || !r.node || current.name) return current;
        current = r.node as NodeLike;
    }
    // Keep the wrapper when the chain is cyclic or exceeds the elision budget.
    return node;
}

function assignedName(node: NodeLike): string | null {
    const r = asRecord(node);
    const isAssign = r.isAssignNode === true
        || (typeof node.type === "string" && node.type === "AssignNode");
    if (!isAssign) return null;
    return targetName(r.targetNode, 0);
}

/**
 * The written-to name of an assignment target. Usually a PropertyNode's `name`,
 * but a component write (`DiffuseColor.w = …`, the alpha writes three emits)
 * targets a SplitNode wrapping the property — so resolve THROUGH the swizzle and
 * rebuild the dotted name, which is what the GLSL line actually reads as.
 * Returns null for computed targets that genuinely have no name.
 */
function targetName(target: unknown, depth: number): string | null {
    if (!target || typeof target !== "object" || depth > 4) return null;
    const t = target as Record<string, unknown>;
    if (typeof t.name === "string" && t.name) return t.name;
    const components = t.components;
    const isSplit = t.isSplitNode === true || t.type === "SplitNode";
    if (isSplit && typeof components === "string" && components) {
        const inner = targetName(t.node, depth + 1);
        if (inner) return `${inner}.${components}`;
    }
    return null;
}
const typeName = (node: NodeLike): string => (typeof node.type === "string" && node.type ? node.type : "Node");
const getOp = (node: NodeLike): string | null => {
    const op = asRecord(node).op;
    return typeof op === "string" ? op : null;
};
const getAttributeName = (node: NodeLike): string | null => {
    const n = asRecord(node).attributeName;
    return typeof n === "string" ? n : null;
};
/** True for value-carrying leaf nodes (uniforms/consts/inputs). */
function isInputStructural(node: NodeLike): boolean {
    const n = asRecord(node);
    return n.isInputNode === true || (("value" in n) && ("precision" in n));
}

/** The target type of a ConvertNode (`convertTo`, e.g. "float"), or null. Duck-typed. */
function convertTargetType(node: NodeLike): string | null {
    const to = asRecord(node).convertTo;
    return typeof to === "string" && to ? to : null;
}

/** Prettifies a TSL type name for a title: "float" → "Float", "vec3" → "Vec3". */
function typeTitle(type: string): string {
    return type.charAt(0).toUpperCase() + type.slice(1);
}

/**
 * A TSL `Fn(() => …)` DEFINITION node — it carries the body closure directly in
 * `jsFunc` (unlike a ShaderCallNodeInternal, which is a *call* to one and holds
 * a `shaderNode`). These have no `type`, so without this they render as a bare
 * "Node". Returns the function name (from its `layout`, else its own `name`),
 * `""` for an anonymous `Fn(() => …)`, or null when it isn't an Fn definition.
 */
function fnDefinitionName(node: NodeLike): string | null {
    const r = asRecord(node);
    if (typeof r.jsFunc !== "function") return null;
    const layout = r.layout as { name?: unknown } | null | undefined;
    return (typeof layout?.name === "string" && layout.name)
        || (typeof node.name === "string" && node.name) || "";
}

/** True for a TSL compute node (`Fn(...).compute(count)` / ComputeNode). */
function isComputeNode(node: NodeLike): boolean {
    const r = asRecord(node);
    return r.isComputeNode === true || typeName(node) === "ComputeNode";
}

/** The closure that carries a node's function body, if any (a TSL `Fn`'s
 *  `jsFunc`, or a ShaderCallNodeInternal's `shaderNode.jsFunc`). */
function fnClosureOf(node: unknown): ((...a: unknown[]) => unknown) | null {
    const r = node as Record<string, unknown> | null | undefined;
    if (!r || typeof r !== "object") return null;
    if (typeof r.jsFunc === "function") return r.jsFunc as never;
    const shaderNode = r.shaderNode as Record<string, unknown> | null | undefined;
    if (shaderNode && typeof shaderNode.jsFunc === "function") return shaderNode.jsFunc as never;
    // A LoopNode keeps its BODY as the last entry of `params` (three calls it
    // while building — LoopNode.js — and never drops it), so the loop's code is
    // still readable when the graph is inspected. It is either the authored
    // arrow function or a TSL `Fn` proxy, whose closure sits on its shaderNode
    // like any other Fn. Recognised by the trailing function, not by a type
    // flag: anything shaped this way carries readable code.
    const params = r.params;
    if (Array.isArray(params) && params.length) {
        const body = params[params.length - 1];
        if (typeof body === "function") {
            const inner = (body as { shaderNode?: { jsFunc?: unknown } }).shaderNode?.jsFunc;
            return (typeof inner === "function" ? inner : body) as never;
        }
    }
    return null;
}

/**
 * Cheap presence check: does this node carry readable function/expression code?
 * A TSL `Fn` (jsFunc closure), a ShaderCallNodeInternal (its shaderNode's
 * closure), or an ExpressionNode (raw `snippet`). Does NOT stringify — callers
 * pull the actual text lazily via {@link getFunctionSource}.
 */
export function hasFunctionSource(node: unknown): boolean {
    const r = node as Record<string, unknown> | null | undefined;
    if (!r || typeof r !== "object") return false;
    return fnClosureOf(node) !== null || typeof r.snippet === "string";
}

/**
 * The node's function/expression **source code** — the JS closure's
 * `toString()` (a TSL `Fn` body, as authored: readable on source builds,
 * minified on bundled pages), or an ExpressionNode's raw snippet. Null when the
 * node carries neither. Called ON DEMAND (code panel selection, AI graph read)
 * — never eagerly during graph building.
 */
export function getFunctionSource(node: unknown): string | null {
    const fn = fnClosureOf(node);
    if (fn) {
        try {
            const src = Function.prototype.toString.call(fn);
            return typeof src === "string" && src.length ? src : null;
        } catch {
            return null;
        }
    }
    const snippet = (node as { snippet?: unknown })?.snippet;
    return typeof snippet === "string" && snippet ? snippet : null;
}

function outSocket(node: NodeLike): Socket {
    // A ConvertNode outputs its target type — label the output with it (the
    // "to" side; the input carries the "from" type — see the walk).
    const convertTo = convertTargetType(node);
    if (convertTo) return { id: "out", name: convertTo, kind: "data", type: toDataType(convertTo) };
    // A SplitNode swizzles specific components (e.g. "x", "xy", "rgb") — surface
    // them as the output label instead of a generic "out".
    const r = asRecord(node);
    const name = r.isSplitNode && typeof r.components === "string" ? r.components : "out";
    return { id: "out", name, kind: "data", type: toDataType(node.nodeType) };
}

/**
 * There is deliberately NO length cap on an origin. Shortening a name in the
 * MODEL throws the information away for everyone — the AI reading the graph over
 * MCP, a search over titles — to solve what is purely a rendering problem, and
 * it cannot know how much room the card has anyway. The layout already reserves
 * label width up to CONTENT_MAX_W (layout.ts), so truncation belongs in CSS,
 * where it can respond to the actual width.
 */

/**
 * A name this node carries ITSELF, or null if it only describes an operation.
 * Deliberately not `nodeTitle`: "Multiply" is a title but says nothing about
 * WHICH value, and treating it as an origin would make the search stop at the
 * first operator it met.
 */
function selfName(node: NodeLike): string | null {
    const r = asRecord(node);
    const t = typeName(node);
    if (r.isPropertyNode === true && node.name) return node.name;
    const assign = assignedName(node);
    if (assign) return assign;
    const attr = getAttributeName(node);
    if (attr) return attr;
    const scope = r.scope;
    if (typeof scope === "string" && scope) return `${stripNodeSuffix(t)}.${scope}`;
    const refProp = r.property;
    if (typeof refProp === "string" && refProp && t.endsWith("ReferenceNode")) {
        return `${t.slice(0, -"ReferenceNode".length) || "Ref"}.${refProp}`;
    }
    if (node.name && (t === "UniformNode" || r.isVarNode === true)) return node.name;
    return null;
}

/**
 * The named value this node DIRECTLY operates on, or null.
 *
 * Deliberately one hop, and deliberately unanimous. Walking further upstream
 * looks helpful and is not: everything eventually reaches a named property, so
 * a whole arithmetic chain inherits one name and reads
 * "Subtract – IOR" / "Add – IOR" / "Divide – IOR" / "Multiply – IOR" — four
 * cards distinguished by nothing. Only the node actually fed by IOR has earned
 * that suffix.
 *
 * And a binary op has TWO inputs. When both are named there is no principled
 * reason to print one of them (it was previously whichever `getChildNodes`
 * happened to yield first), so it prints neither — "Multiply" alone is honest.
 */
function originName(node: NodeLike): string | null {
    const names = new Set<string>();
    for (const { child } of getChildNodes(node)) {
        const named = selfName(elideAnonymousVars(child));
        if (named) names.add(named);
    }
    return names.size === 1 ? [...names][0]! : null;
}

/** Appends where a generic operation's value came from, e.g. "Multiply –
 *  Material.opacity". Full name: the card ellipsizes it in CSS if it must. */
function withOrigin(title: string, node: NodeLike): string {
    const origin = originName(node);
    return origin ? `${title} – ${origin}` : title;
}

export function nodeTitle(node: NodeLike): string {
    const op = getOp(node);
    if (op) return withOrigin(OPERATOR_NAMES[op] ?? `Operator "${op}"`, node);
    // MathNode/TempNode carry the operation name in `.method` (e.g. "max",
    // "smoothstep", "mix") — surface it directly.
    const method = asRecord(node).method;
    if (typeof method === "string" && method) return withOrigin(method, node);
    // ConvertNode: title the conversion target, e.g. "Convert to Float".
    const convertTo = convertTargetType(node);
    if (convertTo) return withOrigin(`Convert to ${typeTitle(convertTo)}`, node);
    const t = typeName(node);
    // Split/Join restructure a value without changing what it MEANS, so on their
    // own they are the least informative cards in the graph.
    if (t === "SplitNode" || t === "JoinNode") return withOrigin(stripNodeSuffix(t), node);
    // Compute kernels (Fn(...).compute(n)) — surface as such, with a name/count.
    if (isComputeNode(node)) {
        const count = asRecord(node).count;
        const suffix = typeof count === "number" ? ` ×${count}` : "";
        return (node.name ? `Compute: ${node.name}` : "Compute") + suffix;
    }
    // ExpressionNode wraps a raw shader snippet (e.g. "discard", or a GLSL/WGSL
    // expression) — show the snippet itself, that's the whole meaning of the node.
    const snippet = asRecord(node).snippet;
    if (typeof snippet === "string" && snippet) {
        return snippet.length <= 32 ? snippet : snippet.slice(0, 31) + "…";
    }
    const attr = getAttributeName(node);
    if (attr) return `Attribute: ${attr}`;
    if (t === "AttributeNode") return "Attribute";
    if (t === "TextureNode") return "Texture";
    if (t === "VaryingNode") return "Varying";
    // A SubBuildNode names the sub-build it stands for ("VERTEX", "FRAGMENT") —
    // that name IS the card's meaning; without it the vertex and fragment
    // sub-builds render as two identical "SubBuild" cards.
    if (asRecord(node).isSubBuildNode === true || t === "SubBuildNode") {
        return node.name ? `SubBuild: ${node.name}` : "SubBuild";
    }
    if (t === "UniformNode") return node.name ? `Uniform: ${node.name}` : "Uniform";
    if (t === "ConstNode") return "Constant";
    // A PropertyNode NAMES a shader variable ("Roughness", "DiffuseColor", …) and
    // that name is the entire meaning of the card — without it a compiled material
    // renders a dozen identical "Property" cards.
    if (asRecord(node).isPropertyNode === true || t === "PropertyNode") {
        return node.name ? `Property: ${node.name}` : "Property";
    }
    // A pass-texture read (postprocessing input, e.g. pass.getTextureNode(
    // "output")) is an Input structurally — title WHAT it reads instead of a
    // generic "Input" (must come before the isInputStructural fallback).
    if (asRecord(node).isPassTextureNode === true) {
        const textureName = asRecord(node).textureName;
        return typeof textureName === "string" && textureName ? `Pass: ${textureName}` : "Pass Texture";
    }
    // A PassNode renders a scene into a target (a postprocessing render pass) —
    // distinct from a getTextureNode read of it (isPassTextureNode, above). Its
    // constructor-name `type` is often absent/minified, so key off the flag.
    if (asRecord(node).isPassNode === true) return "Pass";
    if (isInputStructural(node)) return "Input";
    if (t.includes("Function")) return node.name ? `Fn: ${node.name}` : "Function";
    // Reference nodes read a property off an external object each render, e.g.
    // RendererReferenceNode('toneMappingExposure') — title what they reference.
    const refProp = asRecord(node).property;
    if (typeof refProp === "string" && refProp && t.endsWith("ReferenceNode")) {
        const owner = t.slice(0, -"ReferenceNode".length) || "Ref";
        return `${owner}.${refProp}`;
    }
    // An AssignNode IS a statement — "Clearcoat = …". Its target names it, so
    // titling by the target turns a compiled material's wall of identical
    // "Assign" cards into the shader body as written.
    const assignTarget = assignedName(node);
    if (assignTarget) return `${assignTarget} =`;
    // Scoped accessor nodes read ONE named thing off a subject: MaterialNode
    // ({scope: "roughness"}), and the same pattern on CameraNode / ObjectNode /
    // NormalNode / PositionNode. The scope is the whole meaning, so without it a
    // compiled material shows a dozen indistinguishable "Material" cards. Dotted
    // to match the reference-node style already in the graph
    // ("Material.emissiveIntensity", "Renderer.toneMappingExposure").
    const scope = asRecord(node).scope;
    if (typeof scope === "string" && scope) return `${stripNodeSuffix(t)}.${scope}`;
    // These wrap other nodes and report their type as the generic "Node"; use
    // their duck-typed flags so they don't all render as "Node".
    const r = asRecord(node);
    if (r.isShaderCallNodeInternal) {
        // Named Fns expose a name via the shader node / its layout; anonymous
        // `Fn(() => …)` closures carry none, so fall back to a generic label.
        const shaderNode = r.shaderNode as Record<string, unknown> | undefined;
        const layout = shaderNode?.layout as { name?: unknown } | null | undefined;
        const fnName =
            (typeof layout?.name === "string" && layout.name) ||
            (typeof shaderNode?.name === "string" && shaderNode.name) ||
            "";
        return fnName ? `Fn: ${fnName}` : "Fn Call";
    }
    if (r.isVarNode) return node.name ? `Var: ${node.name}` : "Var";
    // A raw `Fn(() => …)` definition node (has the body closure but no `type`):
    // otherwise indistinguishable from a bare "Node".
    const fnName = fnDefinitionName(node);
    if (fnName !== null) return fnName ? `Fn: ${fnName}` : "Fn";
    return stripNodeSuffix(t);
}

function nodeCategory(node: NodeLike): string {
    if (getOp(node)) return "math";
    if (typeof asRecord(node).method === "string") return "math";
    const t = typeName(node);
    if (t === "TextureNode" || asRecord(node).isPassTextureNode === true) return "texture";
    if (t === "AttributeNode" || getAttributeName(node)) return "attribute";
    if (t === "VaryingNode") return "varying";
    if (t === "UniformNode" || t === "ConstNode" || isInputStructural(node)) return "input";
    if (t.includes("Function")
        || asRecord(node).isShaderCallNodeInternal
        || isComputeNode(node)
        || fnDefinitionName(node) !== null) return "function";
    if (asRecord(node).isVarNode) return "varying";
    return "node";
}

/** TSL doc names for operator symbols (threejs.org/docs/?q=tsl#TSL.<name>). */
const TSL_DOC_OPS: Record<string, string> = {
    "+": "add", "-": "sub", "*": "mul", "/": "div", "%": "mod",
    "==": "equal", "!=": "notEqual", "<": "lessThan", ">": "greaterThan",
    "<=": "lessThanEqual", ">=": "greaterThanEqual",
    "&&": "and", "||": "or", "!": "not",
};

/** MathNode-style `.method` names that actually have a TSL docs entry.
 *  `.method` also carries INTERNAL operations (e.g. "subBuild") — passing it
 *  through unchecked produced dead links like `#TSL.subBuild`. */
const DOCUMENTED_TSL_METHODS = new Set([
    // MathNode unary/binary/ternary operations (three/src/nodes/math/MathNode.js)
    "all", "any", "abs", "acos", "asin", "atan", "atan2", "cbrt", "ceil", "clamp",
    "cos", "cross", "degrees", "dFdx", "dFdy", "difference", "distance", "dot",
    "equals", "exp", "exp2", "faceForward", "floor", "fract", "fwidth",
    "inverseSqrt", "length", "lengthSq", "log", "log2", "max", "min", "mix",
    "mod", "negate", "normalize", "oneMinus", "pow", "pow2", "pow3", "pow4",
    "radians", "rand", "reciprocal", "reflect", "refract", "round", "saturate",
    "sign", "sin", "smoothstep", "sqrt", "step", "tan", "transformDirection",
    "transpose", "trunc",
    // documented non-MathNode methods that surface the same way
    "remap", "remapClamp", "rotate",
]);

/** The TSL identifier documented at `threejs.org/docs/?q=tsl#TSL.<name>`, if known. */
function tslDocsName(node: NodeLike): string | undefined {
    const r = asRecord(node);
    // Math/temp nodes carry the TSL function name directly (mix, acosh, step, …)
    // — but only link the ones that are actually documented.
    if (typeof r.method === "string" && r.method) {
        return DOCUMENTED_TSL_METHODS.has(r.method) ? r.method : undefined;
    }
    const op = getOp(node);
    if (op) return TSL_DOC_OPS[op];
    if (isComputeNode(node)) return "compute";
    if (fnDefinitionName(node) !== null) return "Fn";
    const attr = getAttributeName(node);
    if (attr === "uv") return "uv";
    const t = typeName(node);
    if (t === "AttributeNode" || attr) return "attribute";
    if (t === "TextureNode" || r.isTextureNode) return "texture";
    if (t === "VaryingNode") return "varying";
    if (r.isUniformNode) return "uniform";
    if (r.isConstNode) {
        // Constants are documented under their factory (float/color/vec3/…).
        const v = r.value;
        if (typeof v === "number") return "float";
        if (typeof v === "boolean") return "bool";
        const vv = v as Record<string, unknown> | null | undefined;
        if (vv?.isColor) return "color";
        if (vv?.isVector2) return "vec2";
        if (vv?.isVector3) return "vec3";
        if (vv?.isVector4) return "vec4";
        return "float";
    }
    return undefined;
}

/** Docs deep-link: `?q=<name>` pre-filters the docs list to the term, so the
 *  page is useful even when the `#TSL.<name>` anchor doesn't resolve exactly. */
function tslDocsUrl(name: string): string {
    return `https://threejs.org/docs/?q=${encodeURIComponent(name)}#TSL.${name}`;
}

function buildMeta(node: NodeLike): Record<string, unknown> | undefined {
    // Keep the raw node for the context menu (copy-as-JSON / log to console).
    const meta: Record<string, unknown> = { uuid: node.uuid, nodeType: node.nodeType, node };
    // three.js TSL documentation for this node kind (the "Open documentation"
    // context-menu item). Known identifiers link directly; unknown node kinds
    // link the docs SEARCH for a name derived from the type ("StackNode" →
    // ?q=stack#TSL.stack) — filtered results instead of a dead anchor.
    const docsName = tslDocsName(node);
    if (docsName) {
        meta.docsUrl = tslDocsUrl(docsName);
    } else {
        const t = typeName(node);
        if (t && t !== "Node") {
            const base = stripNodeSuffix(t);
            meta.docsUrl = tslDocsUrl(base.charAt(0).toLowerCase() + base.slice(1));
        }
    }
    // Expression snippet: the title shows it (truncated at 32) — keep the full
    // text in meta so the card/tooltip can surface long expressions in full.
    const snippet = asRecord(node).snippet;
    if (typeof snippet === "string" && snippet.length > 32) meta.value = snippet;
    if (isInputStructural(node)) {
        const value = asRecord(node).value;
        const preview = valuePreview(value);
        if (preview !== undefined) meta.value = preview;
        // Keep the underlying node so the inspection service can build an editable
        // IProperty for its `.value`. Uniforms are live (upload next frame);
        // constants are baked into the shader so the inspection service recompiles
        // the material on commit (see forceNodeMaterialRecompile).
        const editable = isUniformStructural(node) || asRecord(node).isConstNode === true;
        if (editable && isEditableValue(value)) {
            meta.editableNode = node;
            // Textures render a fixed-size thumbnail — flag so layout reserves the
            // room (and the card doesn't reflow when the image finishes loading).
            if (value && typeof value === "object" && (value as { isTexture?: boolean }).isTexture) {
                meta.mediaValue = true;
            }
        }
    }
    const op = getOp(node);
    if (op) meta.op = op;
    const attr = getAttributeName(node);
    if (attr) meta.attributeName = attr;
    // (ContextNode carries no value row — clicking the card reveals the live
    // node in the Inspector, where the full context object reads better than
    // any string baked into the card.)
    // LoopNode: the loop shape lives in plain param objects ({start, end,
    // condition, type}) — preview it ("int <"); the start/end NODES arrive as
    // input sockets via the walker's array-object descent.
    if (typeName(node) === "LoopNode" && meta.value === undefined) {
        const params = asRecord(node).params;
        if (Array.isArray(params)) {
            const dims = params
                .filter((p): p is Record<string, unknown> =>
                    !!p && typeof p === "object" && !Array.isArray(p) && typeof (p as Record<string, unknown>).condition === "string")
                .map((p) => `${typeof p.type === "string" ? p.type : "int"} ${p.condition}`);
            if (dims.length) meta.value = dims.join(", ");
        }
    }
    // RenderOutputNode: the frame's output transform — color space + tone
    // mapping are exactly what you want to read off this node.
    if (asRecord(node).isRenderOutputNode === true) {
        const colorSpace = asRecord(node).outputColorSpace;
        const toneMapping = toneMappingName(asRecord(node)._toneMapping);
        const parts = [
            typeof colorSpace === "string" && colorSpace ? colorSpace : null,
            toneMapping,
        ].filter(Boolean);
        if (parts.length) meta.value = parts.join(" · ");
    }
    return meta;
}

/** three's tone-mapping enum, by value (stable across versions). */
function toneMappingName(value: unknown): string | null {
    switch (value) {
        case 0: return "NoToneMapping";
        case 1: return "Linear";
        case 2: return "Reinhard";
        case 3: return "Cineon";
        case 4: return "ACESFilmic";
        case 5: return "Custom";
        case 6: return "AgX";
        case 7: return "Neutral";
        default: return null;
    }
}

/** True for a live UniformNode (editable); ConstNodes are baked & read-only. */
function isUniformStructural(node: NodeLike): boolean {
    return typeName(node) === "UniformNode" || asRecord(node).isUniformNode === true;
}

/** Value kinds we can edit inline via the reused Inspector property renderers. */
function isEditableValue(value: unknown): boolean {
    if (typeof value === "number" || typeof value === "boolean") return true;
    if (value && typeof value === "object") {
        const v = value as Record<string, unknown>;
        // Textures reuse the Inspector's TextureProperty (thumbnail) — detected by
        // value, so no special property type is needed.
        return !!(v.isColor || v.isVector2 || v.isVector3 || v.isVector4 || v.isTexture);
    }
    return false;
}

/** A short, human-readable preview of a leaf node's value. */
function valuePreview(value: unknown): string | undefined {
    if (value === null || value === undefined) return undefined;
    if (typeof value === "number") return formatNumber(value);
    if (typeof value === "boolean") return String(value);
    if (typeof value === "string") return value;
    if (typeof value !== "object") return undefined;
    const v = value as Record<string, unknown> & { getHexString?: () => string };
    if (v.isColor && typeof v.getHexString === "function") return `#${v.getHexString()}`;
    if (v.isVector2) return `(${fmt(v.x)}, ${fmt(v.y)})`;
    if (v.isVector3) return `(${fmt(v.x)}, ${fmt(v.y)}, ${fmt(v.z)})`;
    if (v.isVector4) return `(${fmt(v.x)}, ${fmt(v.y)}, ${fmt(v.z)}, ${fmt(v.w)})`;
    if (v.isTexture) return (v.name as string) || "texture";
    return undefined;
}

const fmt = (n: unknown): string => (typeof n === "number" ? formatNumber(n) : String(n));

function formatNumber(n: number): string {
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(3).replace(/\.?0+$/, "");
}

function stripNodeSuffix(name: string): string {
    const base = name.endsWith("Node") ? name.slice(0, -4) : name;
    return base.length ? base : name;
}

// #region Type mapping & color

/** Maps a TSL `nodeType` string (may be null) to a generic {@link DataType}. */
export function toDataType(nodeType: string | null | undefined): DataType | undefined {
    if (!nodeType) return undefined;
    switch (nodeType) {
        case "float": case "int": case "uint": case "bool":
            return { signature: nodeType, category: "scalar", components: 1 };
        case "vec2": case "ivec2": case "uvec2": case "bvec2":
            return { signature: nodeType, category: "vector", components: 2 };
        case "vec3": case "ivec3": case "uvec3": case "bvec3": case "color":
            return { signature: nodeType, category: "vector", components: 3 };
        case "vec4": case "ivec4": case "uvec4": case "bvec4":
            return { signature: nodeType, category: "vector", components: 4 };
        case "mat2": return { signature: nodeType, category: "matrix", components: 4 };
        case "mat3": return { signature: nodeType, category: "matrix", components: 9 };
        case "mat4": return { signature: nodeType, category: "matrix", components: 16 };
        case "texture": case "cubeTexture": case "sampler":
            return { signature: nodeType, category: "ref" };
        default:
            return { signature: nodeType, category: "unknown" };
    }
}

/** TSL-flavored socket coloring; falls back to the neutral core palette. */
export function tslTypeColor(type: DataType | undefined): string {
    switch (type?.signature) {
        case "float": return "#8ab4f8";
        case "vec2": return "#5bd6c4";
        case "vec3": case "color": return "#f6c453";
        case "vec4": return "#ff8a65";
        case "texture": case "cubeTexture": return "#ce93d8";
        default: return defaultTypeColor(type);
    }
}
